import {
  defaultAutoAffirmOptions,
  defaultOrderExpiryTypeOptions,
  defaultOrderSetLevelOutrightOptions,
  defaultOrderSetLevelStructureOptions,
  defaultOrderSpreadAndPriceTypeOptions,
  defaultQuoteTypeOptions,
  defaultReleaseAllocationOptions,
  defaultReleaseCompetitionOptions,
  defaultReleaseCounterpartyOptions,
  defaultReleaseCoverOptions,
  defaultReleaseCoverSpreadOptions,
  defaultReleaseDirectionOptions,
  defaultReleaseSecurityOptions,
  defaultTradeTypes,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/toggleHandler";
import {
  DEFAULT_EXPIRY_PERIOD_SECONDS,
  convertExpiryPeriodToSeconds,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/expiryDurationHelper";
import {
  ICPSettingsOverride,
  IToggleButton,
  IToggleButtonToReturnBoolean,
  IToggleCounterpartiesButton,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { fetchRequestWrapper } from "@/lib/utilities";
import {
  RequestInfoField,
  RequestInfoStatus,
} from "@/lib/interfaces and Enums/ENUMRequestInformationStatus";
import requestInformationStore from "./requestInformationStore";
import { determineIsOrder } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  isSideAndDirectionEditable,
  isTradeWideSettingEditable,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeWideSettingGating";
import { buildCounterpartiesListFromTrade } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/counterpartiesFromTrade";
import tradeSettingsStore, { ITradeSettingsStore } from "./tradeSettingsStore";
import {
  findThisTradeSettings,
  resolveSettingForCP,
} from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/SelectPanelHelper";
import {
  isSameOverrideFieldValue,
  isTradeLevelFieldPending,
} from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/CPSettingsGridHelper";
import { toast } from "react-toastify";
import featureFlagStore from "@/store/featureFlagStore";
import tradeDataStore from "@/store/tradeDataStore";
import {
  buildCPOverridesFromResponse,
  cloneOverrides,
  IEditPanelSettingsResponse,
  setBoolToggleActiveByValue,
  setToggleActiveByName,
} from "./editSettingsHydrationHelper";

// Re-export for callers that imported these from the ACTIONS file before the
// helper split.
export type {
  IEditPanelSettingsCPResponse,
  IEditPanelSettingsResponse,
} from "./editSettingsHydrationHelper";

export interface ITradeSettings {
  esid: string;
  settings: ITradeSettingsStore;
}

// Edit mode is signalled by presence of an editBaselineOverrides entry — it's
// set by applyEditSettingsResponseToStore on every Edit Panel hydration and
// cleared when the panel closes / the trade leaves edit context. Read-only —
// callers don't need to manage this flag explicitly.
function isInEditMode(esid: string): boolean {
  return (
    tradeSettingsStore.getState().editBaselineOverrides[esid] !== undefined
  );
}

function updateSettingsForReleaseDirectionOnQuoteTypeChange(
  name: string, //"Two-Way" | "One-Way" | "Mid",
  releaseDirection: IToggleButtonToReturnBoolean[],
) {
  let valueToSetActive = "Show";
  if (name == "Two-Way" || name == "Mid") {
    valueToSetActive = "Withhold";
  }
  const releaseDirectionUpdated = releaseDirection.map((option) => ({
    ...option,
    active: option.name == valueToSetActive,
  }));

  return releaseDirectionUpdated;
}

// LQC-1119: decide whether a Side (quoteType) change should leave Direction
// untouched instead of switching it with Side.
//   - Status New (no edit baseline): Direction always follows Side
//     (One-Way → Show, Two-Way → Withhold). Returns false.
//   - Edit Panel (sent trade): switching to Two-Way must NOT flip Direction to
//     "Withhold" — preserve the current value (returns true). Switching back to
//     One-Way still forces "Show" (returns false → standard rule applies).
// Applies identically to the trade-level default row (setActiveToggle) and the
// per-CP override rows (setCPSettingToggleOverride).
function shouldPreserveDirectionOnQuoteTypeChange(
  esid: string,
  newQuoteTypeName: string,
): boolean {
  return isInEditMode(esid) && newQuoteTypeName !== "One-Way";
}

export interface ISetActiveToggle<
  K extends keyof ITradeSettingsStore = keyof ITradeSettingsStore,
> {
  esid: string;
  selectedElement: IToggleButton | IToggleButtonToReturnBoolean;
  stateVariable: IToggleButton[] | IToggleButtonToReturnBoolean[];
  settingField: K;
}

const defaultTradeSettingsAtNew: ITradeSettingsStore = {
  counterpartiesList: [],
  inValidCounterpartiesList: [],
  tradeTypes: defaultTradeTypes,
  expiryTimeValue: DEFAULT_EXPIRY_PERIOD_SECONDS, // total seconds (15m)
  expiryType: defaultOrderExpiryTypeOptions,
  minimumSize: 0,
  quoteType: defaultQuoteTypeOptions,
  releaseDirection: defaultReleaseDirectionOptions,
  releaseSize: 100, // UI percentage — converted to decimal (100 → 1) in generateOutgoingTradeMessage LQC-623
  releaseCompetition: defaultReleaseCompetitionOptions,
  releaseCover: defaultReleaseCoverOptions,
  releaseCoverSpread: defaultReleaseCoverSpreadOptions,
  releaseCounterparty: defaultReleaseCounterpartyOptions,
  autoAffirm: defaultAutoAffirmOptions,
  level: null,
  // referenceAsset: null, 15455 removed
  // referencePrice: null, 15455 removed
  releaseAllocation: defaultReleaseAllocationOptions,
  releaseSecurity: defaultReleaseSecurityOptions,
  // Order-specific settings
  orderSetLevelStructure: defaultOrderSetLevelStructureOptions,
  orderSetLevelOutright: defaultOrderSetLevelOutrightOptions,
  orderSpreadValue: undefined,
  orderSpreadType: defaultOrderSpreadAndPriceTypeOptions,
  orderPriceType: defaultOrderSpreadAndPriceTypeOptions,
  orderPriceValue: undefined,
  cpSettingsOverrides: {},
};

export async function addNewTradeSettingsAtNew({ esid }: { esid: string }) {
  const currentSettings = tradeSettingsStore.getState().tradeSettingsData;

  // const currentCryptoSettingsTrades = currentSettings.find(
  //   (trades) => trades.cryptoID == cryptoID,
  // )?.trades;
  // if (currentCryptoSettingsTrades && currentCryptoSettingsTrades.length > 0) {
  //   const updatedCurrentCryptoSettings = [
  //     ...currentCryptoSettingsTrades,
  //     { esid: esid, setting: defaultTradeSettingsAtNew },
  //   ];

  if (!currentSettings.find((trade) => trade.esid == esid)) {
    const updatedData: ITradeSettings[] = [...currentSettings];
    updatedData.push({
      esid: esid,
      settings: defaultTradeSettingsAtNew,
    });

    if (updatedData) {
      tradeSettingsStore.getState().updateTradeSettingsData(updatedData);
    }
  }
}

export function updatedAllTradeSettings({
  esid,
  newSettings,
}: {
  esid: string;
  newSettings: ITradeSettingsStore;
}) {
  const allTradeSettings = tradeSettingsStore.getState().tradeSettingsData;

  const exists = allTradeSettings.some((t) => t.esid === esid);
  let newData: ITradeSettings[];
  if (!exists) {
    // No prior entry (e.g. Sent trade opened via gear icon without a prior
    // Select Panel / Edit Panel visit this session) — create the entry now.
    newData = [...allTradeSettings, { esid, settings: newSettings }];
  } else {
    newData = allTradeSettings.map((trade) => {
      if (trade.esid !== esid) return trade;
      return newSettings ? { ...trade, settings: newSettings } : { ...trade };
    });
  }
  tradeSettingsStore.getState().updateTradeSettingsData(newData);
}

// HELPER FUNCTIONS used by BOTH the Trade Settings and the CounterParties Settings
// 1. Set Active Toggles in SETTINGS
export function setActiveToggle<K extends keyof ITradeSettingsStore>({
  esid,
  selectedElement,
  stateVariable,
  settingField,
}: ISetActiveToggle<K>) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newValueApplied = stateVariable.map((type) => ({
    ...type,
    active: type.name === selectedElement.name,
  }));

  const newTradeSettings = { ...thisTradeSettings };
  newTradeSettings[settingField] =
    newValueApplied as unknown as ITradeSettingsStore[K];

  if (settingField == "quoteType") {
    // Direction follows Side at status New (One-Way → Show, Two-Way → Withhold).
    // LQC-1119: in the Edit Panel (sent trade) switching the default to Two-Way
    // must NOT flip Direction to "Withhold" — it stays "Show". Switching back to
    // One-Way still forces "Show".
    if (!shouldPreserveDirectionOnQuoteTypeChange(esid, selectedElement.name)) {
      newTradeSettings.releaseDirection =
        updateSettingsForReleaseDirectionOnQuoteTypeChange(
          selectedElement.name,
          thisTradeSettings.releaseDirection,
        );
    }
    // New trade: a CP that FOLLOWS the trade-level Side should also follow the
    // trade-level Direction, so clear its per-CP releaseDirection override and
    // let it fall back to the new global direction.
    //
    // LQC-1119: but a CP with its OWN Side override is broken from the default —
    // its Direction is linked to its OWN Side (a Two-Way CP stays Withhold) and
    // an explicit Direction override (orange border) must survive a default
    // change. Clearing it would wrongly make a Two-Way CP fall back to the
    // One-Way default's "Show". So only clear Direction for CPs without a Side
    // override.
    //
    // Edit mode: leave all overrides alone — they represent explicit per-CP
    // customisation we don't want to clobber.
    if (!isInEditMode(esid)) {
      const overrides = { ...newTradeSettings.cpSettingsOverrides };
      for (const cpCode of Object.keys(overrides)) {
        const cpOverride = { ...overrides[cpCode] };
        if (cpOverride.quoteType === undefined) {
          delete cpOverride.releaseDirection;
        }
        overrides[cpCode] = cpOverride;
      }
      newTradeSettings.cpSettingsOverrides = overrides;
    }
  }

  // When switching to Order mode, force quoteType to One-Way, releaseDirection
  // to Show, and releaseSize (Iceberg) to 100. The iceberg snap only applies
  // when the icebergForOrders flag is OFF (LQC-1938): flag ON, Orders keep
  // their authored iceberg; flag OFF, the input is disabled for Orders so any
  // pre-existing sub-100 value from an RFQ session must snap to 100 so the
  // disabled cell never displays a stale RFQ value (mirrors the Side rule).
  //
  // The four release* settings (releaseCompetition, releaseCover,
  // releaseCoverSpread, releaseCounterparty) never apply to Orders (not
  // flag-gated) and are disabled in the grid for Order trades. Snap them to
  // defaults on the switch so the disabled cells don't display stale RFQ
  // values.
  if (settingField == "tradeTypes" && selectedElement.name === "Order") {
    const icebergForOrders = featureFlagStore.getState().icebergForOrders;
    newTradeSettings.quoteType = newTradeSettings.quoteType.map((type) => ({
      ...type,
      active: type.name === "One-Way",
    }));
    // Also update releaseDirection to match One-Way default (Show)
    newTradeSettings.releaseDirection =
      updateSettingsForReleaseDirectionOnQuoteTypeChange(
        "One-Way",
        newTradeSettings.releaseDirection,
      );
    if (
      !icebergForOrders &&
      typeof newTradeSettings.releaseSize === "number" &&
      newTradeSettings.releaseSize < 100
    ) {
      newTradeSettings.releaseSize = 100;
    }
    newTradeSettings.releaseCompetition = defaultReleaseCompetitionOptions.map(
      (o) => ({ ...o }),
    );
    newTradeSettings.releaseCover = defaultReleaseCoverOptions.map((o) => ({
      ...o,
    }));
    newTradeSettings.releaseCoverSpread = defaultReleaseCoverSpreadOptions.map(
      (o) => ({ ...o }),
    );
    newTradeSettings.releaseCounterparty =
      defaultReleaseCounterpartyOptions.map((o) => ({ ...o }));
    // New trade: clear all per-CP overrides for fields that get reset on the
    // Order switch (quoteType, releaseDirection, releaseSize, plus the two
    // per-CP-overridable release* fields) so per-CP rows fall back to the
    // trade-level Order defaults. releaseSize clears even with icebergForOrders
    // ON: the CP Overrides grid is Order-locked, so a lingering RFQ-session
    // override would invisibly win over the trade-level value at send
    // (LQC-1938). Edit mode: leave existing overrides alone (matches the
    // existing pattern — re-selecting a recalled CP restores its prior
    // settings).
    if (!isInEditMode(esid)) {
      const overrides = { ...newTradeSettings.cpSettingsOverrides };
      for (const cpCode of Object.keys(overrides)) {
        const cpOverride = { ...overrides[cpCode] };
        delete cpOverride.quoteType;
        delete cpOverride.releaseDirection;
        delete cpOverride.releaseSize;
        delete cpOverride.releaseCompetition;
        delete cpOverride.releaseCover;
        delete cpOverride.releaseCoverSpread;
        overrides[cpCode] = cpOverride;
      }
      newTradeSettings.cpSettingsOverrides = overrides;
    }
  }

  // No edit-mode broadcast: trade-level changes flow to per-CP rows via
  // resolveSettingForCP fallback (same as new-trade mode). A CP only gets an
  // explicit override when the user edits that CP's row directly.

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

//2. Update the settings: Need to make sure BOTH the trade type is set to one-way AND the Direction withheld is set to SHOW.
// Context - The Leg endpoint has returned data that means the trade settings need to be overwritten.
export function setTradeSettingsToOneWayDirectionShown({
  esid,
}: {
  esid: string;
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newTradeSettings = { ...thisTradeSettings };

  newTradeSettings.quoteType = newTradeSettings.quoteType.map((type) => ({
    ...type,
    active: type.name === "One-Way", // Will set others to false
  }));

  newTradeSettings.releaseDirection = newTradeSettings.releaseDirection.map(
    (option) => ({
      ...option,
      active: option.name == "Show", // Will set others to false
    }),
  );

  // Clear per-CP quoteType and releaseDirection overrides (NPV forces these globally)
  // Keep the CP entry even if empty (so bank stays in CP Overrides)
  const currentOverrides = { ...newTradeSettings.cpSettingsOverrides };
  for (const cpCode of Object.keys(currentOverrides)) {
    const cpOverride = { ...currentOverrides[cpCode] };
    delete cpOverride.quoteType;
    delete cpOverride.releaseDirection;
    currentOverrides[cpCode] = cpOverride;
  }
  newTradeSettings.cpSettingsOverrides = currentOverrides;

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

// LQC-1704: Cover (releaseCover) and Cover Spread (releaseCoverSpread) only
// apply when sending to more than one CP. Snap both back to their hardcoded
// defaults (Advise Cover → "No", Cover Spread → "No") and drop any per-CP
// releaseCover / releaseCoverSpread overrides so each CP falls back to the
// now-default trade-level value. Pure — returns a new settings object.
// (LQC-1705: releaseCoverSpread is now per-CP, so its overrides are cleared too.)
export function resetCoverSettingsToDefault(
  settings: ITradeSettingsStore,
): ITradeSettingsStore {
  const next: ITradeSettingsStore = {
    ...settings,
    releaseCover: defaultReleaseCoverOptions.map((o) => ({ ...o })),
    releaseCoverSpread: defaultReleaseCoverSpreadOptions.map((o) => ({ ...o })),
  };

  let overridesChanged = false;
  const overrides: Record<string, ICPSettingsOverride> = {};
  for (const [code, ov] of Object.entries(settings.cpSettingsOverrides)) {
    if (ov.releaseCover !== undefined || ov.releaseCoverSpread !== undefined) {
      const copy = { ...ov };
      delete copy.releaseCover;
      delete copy.releaseCoverSpread;
      overrides[code] = copy;
      overridesChanged = true;
    } else {
      overrides[code] = ov;
    }
  }
  if (overridesChanged) next.cpSettingsOverrides = overrides;

  return next;
}

// 3. Update ALL counterparties for the trade.
export function setTradeSettingsSelectedCounterparties({
  esid,
  newCounterpartyList,
}: {
  esid: string;
  newCounterpartyList: IToggleCounterpartiesButton[];
}) {
  if (newCounterpartyList.length == 0) {
    return;
  }

  newCounterpartyList.sort((a, b) => {
    const cpNameA = a.code.toUpperCase();
    const cpNameB = b.code.toUpperCase();

    if (cpNameA < cpNameB) {
      return -1;
    }
    if (cpNameA > cpNameB) {
      return 1;
    }
    return 0;
  });

  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }
  const prevActiveCount = thisTradeSettings.counterpartiesList.filter(
    (cp) => cp.active,
  ).length;
  const newActiveCount = newCounterpartyList.filter((cp) => cp.active).length;

  const newTradeSettings = { ...thisTradeSettings };
  newTradeSettings.counterpartiesList = newCounterpartyList;

  // New trade: clean up per-CP overrides for deselected counterparties; if only
  // 1 CP remains active clear all overrides so defaults apply directly. Edit
  // mode skips both — overrides for deselected CPs (including recalled ones)
  // stay around so re-selecting restores their settings, and the single-active
  // case is just multi-CP-mode-with-one-row, not a special "settings on the
  // global row" case.
  if (!isInEditMode(esid)) {
    const activeCPCodes = new Set(
      newCounterpartyList.filter((cp) => cp.active).map((cp) => cp.code),
    );
    const currentOverrides = { ...newTradeSettings.cpSettingsOverrides };
    let overridesChanged = false;
    for (const cpCode of Object.keys(currentOverrides)) {
      if (!activeCPCodes.has(cpCode)) {
        delete currentOverrides[cpCode];
        overridesChanged = true;
      }
    }
    if (overridesChanged) {
      newTradeSettings.cpSettingsOverrides = currentOverrides;
    }
    if (activeCPCodes.size <= 1) {
      newTradeSettings.cpSettingsOverrides = {};
    }
  }

  // LQC-1704: Cover / Cover Spread require more than one CP. When the user
  // *reduces* the active counterparties down to 1 (or 0) with a non-default
  // value selected, snap both back to their defaults so a stale value isn't
  // stranded on the (now disabled) controls — and would otherwise be sent.
  // Applies in both New and Edit mode. Gated on a genuine reduction
  // (prev > 1 → new <= 1): initial hydration/seeding (prev 0 → 1) must NOT reset,
  // or it would clobber a legitimate single-CP trade's hydrated values.
  if (prevActiveCount > 1 && newActiveCount <= 1) {
    const reset = resetCoverSettingsToDefault(newTradeSettings);
    newTradeSettings.releaseCover = reset.releaseCover;
    newTradeSettings.releaseCoverSpread = reset.releaseCoverSpread;
    newTradeSettings.cpSettingsOverrides = reset.cpSettingsOverrides;
  }

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

export function setTradeSettingsInvalidCounterparties({
  esid,
  invalidCounterpartyList,
}: {
  esid: string;
  invalidCounterpartyList: IToggleCounterpartiesButton[];
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newTradeSettings = { ...thisTradeSettings };
  newTradeSettings.inValidCounterpartiesList = invalidCounterpartyList;
  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

// Clear all active counterparties for a trade (set all cp.active to false and clear their desks)
export function clearAllActiveCounterparties({ esid }: { esid: string }) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newTradeSettings = { ...thisTradeSettings };
  newTradeSettings.counterpartiesList =
    thisTradeSettings.counterpartiesList.map((cp) => ({
      ...cp,
      active: false,
      activeDesks: [],
    }));

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

// Update a numeric setting value (e.g., orderSpreadValue, expiryTypeValue)
// Accepts string for $fr fraction values (e.g., "99-16")
export function setNumericSetting({
  esid,
  settingField,
  value,
}: {
  esid: string;
  settingField: keyof ITradeSettingsStore;
  value: number | string | undefined;
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newTradeSettings = { ...thisTradeSettings };
  (newTradeSettings[settingField] as number | string | undefined) = value;

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

// --- Per-Counterparty Setting Overrides ---

// Set a toggle override for a specific counterparty
export function setCPSettingToggleOverride({
  esid,
  cpCode,
  settingField,
  selectedElement,
  stateVariable,
}: {
  esid: string;
  cpCode: string;
  settingField: keyof ICPSettingsOverride;
  selectedElement: IToggleButton | IToggleButtonToReturnBoolean;
  stateVariable: IToggleButton[] | IToggleButtonToReturnBoolean[];
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newValueApplied = stateVariable.map((type) => ({
    ...type,
    active: type.name === selectedElement.name,
  }));

  const newTradeSettings = { ...thisTradeSettings };
  const currentOverrides = { ...newTradeSettings.cpSettingsOverrides };
  const cpOverride = { ...currentOverrides[cpCode] };
  (cpOverride[settingField] as
    | IToggleButton[]
    | IToggleButtonToReturnBoolean[]) = newValueApplied as
    | IToggleButton[]
    | IToggleButtonToReturnBoolean[];

  // When quoteType changes, decide whether this CP's releaseDirection follows.
  //
  // New trade: Direction switches with Side every time (One-Way → Show,
  // Two-Way → Withhold) — unchanged behaviour.
  //
  // Edit Panel (LQC-1119, sent trade): switching to Two-Way must NOT silently
  // flip a sent Direction to "Withhold" — preserve the CP's current value.
  // Switching back to One-Way still forces "Show" (One-Way always shows
  // direction). See shouldPreserveDirectionOnQuoteTypeChange.
  if (settingField === "quoteType") {
    if (!shouldPreserveDirectionOnQuoteTypeChange(esid, selectedElement.name)) {
      const currentDirection =
        cpOverride.releaseDirection ?? thisTradeSettings.releaseDirection;
      cpOverride.releaseDirection =
        updateSettingsForReleaseDirectionOnQuoteTypeChange(
          selectedElement.name,
          currentDirection,
        );
    }
  }

  currentOverrides[cpCode] = cpOverride;
  newTradeSettings.cpSettingsOverrides = currentOverrides;

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

// Set a numeric override for a specific counterparty (e.g., releaseSize, expiryTimeValue)
export function setCPSettingNumericOverride({
  esid,
  cpCode,
  settingField,
  value,
}: {
  esid: string;
  cpCode: string;
  settingField: keyof ICPSettingsOverride;
  value: number | string | undefined;
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newTradeSettings = { ...thisTradeSettings };
  const currentOverrides = { ...newTradeSettings.cpSettingsOverrides };
  const cpOverride = { ...currentOverrides[cpCode] };
  (cpOverride[settingField] as number | string | undefined) = value;
  currentOverrides[cpCode] = cpOverride;
  newTradeSettings.cpSettingsOverrides = currentOverrides;

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

// Initialize an empty override entry for a CP (used when adding a bank to the overrides section)
export function initCPOverride({
  esid,
  cpCode,
}: {
  esid: string;
  cpCode: string;
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) return;
  // Don't re-init if override already exists
  if (thisTradeSettings.cpSettingsOverrides[cpCode]) return;

  const newTradeSettings = { ...thisTradeSettings };
  const currentOverrides = { ...newTradeSettings.cpSettingsOverrides };
  currentOverrides[cpCode] = {};
  newTradeSettings.cpSettingsOverrides = currentOverrides;

  updatedAllTradeSettings({ esid, newSettings: newTradeSettings });
}

// Clear a single per-CP setting override (or all overrides for a CP if no settingField given)
export function clearCPSettingOverride({
  esid,
  cpCode,
  settingField,
}: {
  esid: string;
  cpCode: string;
  settingField?: keyof ICPSettingsOverride;
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) {
    return;
  }

  const newTradeSettings = { ...thisTradeSettings };
  const currentOverrides = { ...newTradeSettings.cpSettingsOverrides };

  if (settingField) {
    // Clear a single field override
    const cpOverride = { ...currentOverrides[cpCode] };
    delete cpOverride[settingField];
    // Remove the CP entry entirely if no overrides remain
    if (Object.keys(cpOverride).length === 0) {
      delete currentOverrides[cpCode];
    } else {
      currentOverrides[cpCode] = cpOverride;
    }
  } else {
    // Clear all overrides for this CP
    delete currentOverrides[cpCode];
  }

  newTradeSettings.cpSettingsOverrides = currentOverrides;

  updatedAllTradeSettings({
    esid,
    newSettings: newTradeSettings,
  });
}

// Reset all field overrides for a CP back to the trade-level defaults.
//
// LQC-1119: remove the CP entry entirely rather than leaving an empty {} behind.
// The row still renders (every active CP gets a row in Custom mode), and a
// content-free {} shell would otherwise keep counting as a "per-CP override" —
// e.g. keeping the "All" option locked even though nothing diverges. A deleted
// entry resolves to the trade-level defaults via resolveSettingForCP, which is
// exactly what "reset to defaults" means.
export function resetCPOverrideFields({
  esid,
  cpCode,
}: {
  esid: string;
  cpCode: string;
}) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) return;

  const newTradeSettings = { ...thisTradeSettings };
  const currentOverrides = { ...newTradeSettings.cpSettingsOverrides };
  delete currentOverrides[cpCode];
  newTradeSettings.cpSettingsOverrides = currentOverrides;

  updatedAllTradeSettings({ esid, newSettings: newTradeSettings });
}

// Wipe every per-CP override for this trade. Used when the user toggles the
// CP display mode back to "All" on a new trade — they're declaring that no
// per-CP customisation should remain, so any in-flight edits are discarded
// and every CP follows the trade-level defaults via fallback.
export function clearAllCPSettingOverrides({ esid }: { esid: string }) {
  const thisTradeSettings = findThisTradeSettings({ esid });
  if (!thisTradeSettings) return;

  const newTradeSettings = { ...thisTradeSettings };
  newTradeSettings.cpSettingsOverrides = {};

  updatedAllTradeSettings({ esid, newSettings: newTradeSettings });
}

// --- LQC-774: Edit Panel settings hydration ---
// Pure mapping lives in ./editSettingsHydrationHelper.ts — this wrapper
// orchestrates store writes + baseline snapshotting.

// Build a fresh ITradeSettingsStore from the API response and push it to the store.
//
// Edit-mode mental model: the trade-level SETTINGS row ALWAYS reflects the
// hardcoded defaults (defaultTradeSettingsAtNew) for per-CP-overridable fields,
// regardless of CP count. Every CP returned by the API gets an entry in
// cpSettingsOverrides — including recalled/notdealt CPs, so re-selecting them
// later restores their previous settings. Active CPs whose values match the
// hardcoded defaults get an empty override entry; the moment any CP diverges,
// the CP Overrides grid expands and shows every active CP.
//
// CP selection (active flags + deskRestrictions on counterpartiesList) is owned
// by setPanelFromExistingTrade in CPList.tsx — we only touch settings/overrides
// here.
export function applyEditSettingsResponseToStore({
  esid,
  response,
}: {
  esid: string;
  response: IEditPanelSettingsResponse;
}) {
  const existing = findThisTradeSettings({ esid });
  const base: ITradeSettingsStore = existing
    ? { ...existing }
    : { ...defaultTradeSettingsAtNew };

  // Trade-level fields (no per-CP counterpart) — use the API value directly.
  if (response.requestType !== undefined) {
    base.tradeTypes = setToggleActiveByName(
      defaultTradeTypes,
      response.requestType,
    );
  }
  // API sends (expiryPeriod, expiryPeriodType) pairs; the store keeps total
  // seconds (LQC-2170).
  if (response.expiryPeriod !== undefined)
    base.expiryTimeValue =
      convertExpiryPeriodToSeconds(
        response.expiryPeriod,
        response.expiryPeriodType,
      ) ?? base.expiryTimeValue;
  if (response.expiryType !== undefined) {
    base.expiryType = setToggleActiveByName(
      defaultOrderExpiryTypeOptions,
      response.expiryType,
    );
  }
  if (response.minimumSize !== undefined)
    base.minimumSize = response.minimumSize;

  // Per-CP-overridable fields on the trade-level row are forced to hardcoded
  // defaults — they're never used as a CP surrogate in edit mode.
  base.quoteType = defaultQuoteTypeOptions.map((o) => ({ ...o }));
  base.releaseDirection = defaultReleaseDirectionOptions.map((o) => ({ ...o }));
  base.releaseCompetition = defaultReleaseCompetitionOptions.map((o) => ({
    ...o,
  }));
  base.releaseCover = defaultReleaseCoverOptions.map((o) => ({ ...o }));
  // LQC-1705: releaseCoverSpread is now per-CP — reset the trade-level row to the
  // hardcoded default (per-CP divergence is seeded in buildCPOverridesFromResponse).
  base.releaseCoverSpread = defaultReleaseCoverSpreadOptions.map((o) => ({
    ...o,
  }));
  base.releaseSize = defaultTradeSettingsAtNew.releaseSize;

  const cps = response.counterparties ?? [];

  // Trade-only settings whose persisted value is needed for the read-only
  // display in the Edit Panel. These are sent trade-wide on the outgoing
  // payload, so the first CP's value === the trade-level value.
  const firstCp = cps[0];
  base.autoAffirm = setBoolToggleActiveByValue(
    defaultAutoAffirmOptions,
    firstCp?.autoAffirm,
  );
  // API can return null for releaseCounterparty; coerce to undefined so the
  // helper's undefined-guard falls through to defaults.
  base.releaseCounterparty = setBoolToggleActiveByValue(
    defaultReleaseCounterpartyOptions,
    firstCp?.releaseCounterparty ?? undefined,
  );

  const overrides = buildCPOverridesFromResponse(
    cps,
    defaultTradeSettingsAtNew.releaseSize,
  );
  base.cpSettingsOverrides = overrides;

  updatedAllTradeSettings({ esid, newSettings: base });

  // Snapshot the just-applied overrides as the baseline. The CP Overrides grid
  // uses this to colour saved state green and pending edits orange. Presence
  // of this baseline is also the in-edit-mode signal for setActiveToggle /
  // setNumericSetting / setTradeSettingsSelectedCounterparties.
  tradeSettingsStore
    .getState()
    .setEditBaselineForEsid(esid, cloneOverrides(overrides));
  tradeSettingsStore.getState().setEditTradeWideBaselineForEsid(esid, {
    autoAffirm: firstCp?.autoAffirm,
  });
  // Snapshot the just-applied trade-level defaults (hardcoded on hydration) as
  // the starting point for trade-level pending-change colouring.
  tradeSettingsStore
    .getState()
    .setEditTradeLevelBaselineForEsid(esid, snapshotTradeLevelBaseline(base));

  // LQC-1040: snapshot the full set of CP codes the API returned. Used by the
  // iceberg floor rule to distinguish CPs the bank has previously been sent
  // (floor applies) from CPs added during the edit session that have never
  // received the trade (no floor — any value 1–100 is valid).
  tradeSettingsStore.getState().setEditKnownCpCodesForEsid(
    esid,
    cps.map((cp) => cp.counterparty),
  );
}

// Trade-level CP-overridable fields tracked for pending-change colouring.
// (releaseSize is numeric; the rest are toggle arrays cloned element-wise.)
const TRADE_LEVEL_BASELINE_FIELDS = [
  "quoteType",
  "releaseDirection",
  "releaseCompetition",
  "releaseCover",
  "releaseCoverSpread",
  "releaseSize",
] as const;

// Clone the trade-level CP-overridable values into a baseline snapshot. Toggle
// arrays are cloned element-wise so later edits to the live arrays don't mutate
// the baseline; releaseSize is a primitive number copied as-is.
function snapshotTradeLevelBaseline(
  settings: ITradeSettingsStore,
): Partial<ICPSettingsOverride> {
  const snap: Partial<ICPSettingsOverride> = {};
  for (const field of TRADE_LEVEL_BASELINE_FIELDS) {
    const value = settings[field];
    (snap as Record<string, unknown>)[field] = Array.isArray(value)
      ? value.map((o) => ({ ...o }))
      : value;
  }
  return snap;
}

// Advance the baseline to match the current store state. Called after a
// successful PUT /Trade/Execution/Edit so recently-sent edits flip from
// orange (pending) back to green (saved).
export function advanceEditBaselineFromCurrent(esid: string) {
  const current = findThisTradeSettings({ esid });
  if (!current) return;
  const store = tradeSettingsStore.getState();
  store.setEditBaselineForEsid(
    esid,
    cloneOverrides(current.cpSettingsOverrides),
  );
  store.setEditTradeWideBaselineForEsid(esid, {
    autoAffirm: getActiveBooleanValue(current.autoAffirm),
  });
  store.setEditTradeLevelBaselineForEsid(
    esid,
    snapshotTradeLevelBaseline(current),
  );

  // LQC-1040: any CP that's active at PUT time has just been sent the trade,
  // so they become floor-bound for any further edits in the same session.
  // Merge active CP codes into the known set (deduped).
  const activeCpCodes = current.counterpartiesList
    .filter((cp) => cp.active)
    .map((cp) => cp.code);
  const existingKnown = store.editKnownCpCodes[esid] ?? [];
  const merged = Array.from(new Set([...existingKnown, ...activeCpCodes]));
  store.setEditKnownCpCodesForEsid(esid, merged);
}

// Fetch the current server-side trade settings for an already-sent trade and
// hydrate the tradeSettingsAtNew store. Called when the Edit Panel opens.
//
// LQC-774 — ALWAYS refetch on panel open (no idempotency guard). Stale settings
// from a prior edit session are more dangerous than overwriting unsaved in-flight
// edits: if two users open Edit Panel on the same ES, a second GET deliberately
// pastes fresh server state over the first user's local edits rather than
// letting them commit changes against a drifted baseline.
export async function getEditSettingsAndAddToStore({
  esid,
  getAccessTokenSilently,
}: {
  esid: string;
  getAccessTokenSilently: Function;
}) {
  const response: IEditPanelSettingsResponse = await fetchRequestWrapper(
    { url: `/Trade/Execution/Edit/Settings/${esid}` },
    getAccessTokenSilently,
  );
  if (!response) return;
  applyEditSettingsResponseToStore({ esid, response });
}

// LQC-925 — single-CP iceberg PATCH. Fired from the Iceberg cell editor
// (LQC-922) so the OP can adjust a CP's release size from the action summary
// table without opening the edit panel. The editor writes to tradeDataStore
// optimistically before calling this so the cell updates immediately; the
// backend then triggers a MegaPipe push to the affected RP and OP, which
// reconciles the authoritative value through the existing real-time path.
export async function patchSingleCPIcebergSetting({
  esid,
  executionRequestId,
  releaseSize,
  getAccessTokenSilently,
}: {
  esid: string;
  executionRequestId: number | string;
  releaseSize: number; // decimal 0-1
  getAccessTokenSilently: Function;
}) {
  return fetchRequestWrapper(
    {
      url: "/Trade/Execution/Settings",
      method: "PATCH",
      objToSend: {
        esid,
        executionRequestId,
        releaseSize,
      },
    },
    getAccessTokenSilently,
  );
}

// LQC-637 — single-CP release-setting PATCH. Accepting an RP information request
// is the equivalent of flipping that CP's matching Advise toggle to "Yes" and
// hitting Update, so it must persist through the SAME /Trade/Execution/Settings
// endpoint the sidebar Update button uses. Mirrors patchSingleCPIcebergSetting:
// a minimal per-ER settings PATCH carrying just the one boolean field.
export async function patchSingleCPReleaseSetting({
  esid,
  executionRequestId,
  settingField,
  value,
  getAccessTokenSilently,
}: {
  esid: string;
  executionRequestId: number | string;
  settingField:
    | "releaseDirection"
    | "releaseCompetition"
    | "releaseCover"
    | "releaseCoverSpread";
  value: boolean;
  getAccessTokenSilently: Function;
}) {
  return fetchRequestWrapper(
    {
      url: "/Trade/Execution/Settings",
      method: "PATCH",
      objToSend: {
        esid,
        executionRequestId,
        [settingField]: value,
      },
    },
    getAccessTokenSilently,
  );
}

// LQC-658 — RP "Request Information" POST. From the RP perspective a trade has a
// single execution request, so the RP can ask the OP to release a specific piece
// of info on it (Direction / Competition / Cover / Cover Spread). esid +
// executionRequestId are ALWAYS sent; only the field(s) actually requested are
// included in the body. The OP's response flows back through the real-time push,
// so this just fires the request.
export interface IRequestInformationFields {
  direction?: boolean;
  competition?: boolean;
  cover?: boolean;
  coverSpread?: boolean;
}

export async function requestExecutionRequestInformation({
  esid,
  executionRequestId,
  fields,
  getAccessTokenSilently,
}: {
  esid: string;
  executionRequestId: number | string;
  fields: IRequestInformationFields;
  getAccessTokenSilently: Function;
}) {
  // Only the requested (truthy) fields are sent — an unset/false field means
  // "not requested" and is omitted entirely.
  const requested = Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value === true),
  );
  if (Object.keys(requested).length === 0) return;

  const response = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Request/Information",
      method: "PATCH",
      objToSend: { esid, executionRequestId, ...requested },
    },
    getAccessTokenSilently,
  );

  // LQC-637 — optimistically stamp each requested field so the RP's status word
  // flips to "Requested" immediately; the SignalR push reconciles the authoritative
  // True/Rejected response.
  for (const field of Object.keys(requested) as RequestInfoField[]) {
    requestInformationStore
      .getState()
      .setFieldStatus(
        esid,
        executionRequestId,
        field,
        RequestInfoStatus.Requested,
      );
  }

  return response;
}

// Helper: active boolean value from a boolean-valued toggle array.
function getActiveBooleanValue(
  arr: IToggleButtonToReturnBoolean[] | undefined,
): boolean | undefined {
  return arr?.find((o) => o.active)?.booleanValue;
}

// Build and fire PATCH /Trade/Execution/Settings for each active CP that has
// at least one changed field. Compares current settings against the baseline
// snapshot stored at GET time (editBaselineOverrides / editTradeWideBaselineByEsid)
// so only genuinely changed values are sent — CPs with nothing changed are
// skipped entirely. When no baseline exists (New trade), all fields are sent.
//
// Editable fields in the sidebar are: autoAffirm (trade-wide, identical for
// every CP), plus quoteType / releaseDirection / releaseCover /
// releaseCompetition / releaseCoverSpread / releaseSize per-CP. Other fields are
// not surfaced in the sidebar UI.
// LQC-1705: releaseCoverSpread moved from trade-wide to per-CP.
// LQC-2271: quoteType (Side) + releaseDirection (Direction) added, pre-Awarded
// and RFQ only.
export async function patchSidebarTradeSettings({
  esid,
  getAccessTokenSilently,
}: {
  esid: string;
  getAccessTokenSilently: Function;
}) {
  const trade = tradeDataStore
    .getState()
    .tradeData.find((t) => t.esid === esid);
  const settingsEntry = tradeSettingsStore
    .getState()
    .tradeSettingsData.find((t) => t.esid === esid);
  if (!trade || !settingsEntry) return [];

  const settings = settingsEntry.settings;
  const storeState = tradeSettingsStore.getState();
  const cpBaselines = storeState.editBaselineOverrides[esid];
  const tradeWideBaseline = storeState.editTradeWideBaselineByEsid[esid];
  const tradeLevelBaseline = storeState.editTradeLevelBaselineByEsid[esid];

  // Trade-wide fields — send only if (a) the setting is still editable at this
  // status AND (b) it changed from the baseline (or no baseline).
  //
  // The status gate mirrors the backend guard exactly (Auto Affirm editable up
  // to Awarded/Working; Cover Spread up to Affirmed — see tradeWideSettingGating).
  // Without it, an unchanged field can still leak into the payload: the baseline
  // stores the raw API value (which may be null/undefined) while currentAutoAffirm
  // resolves to a concrete default boolean, so the inequality fires a false
  // positive. The backend then sees an autoAffirm value at a frozen status and
  // rejects the whole PATCH — blocking unrelated edits like Cover Spread. ANDing
  // with isTradeWideSettingEditable strips the field whenever the status can't
  // change it, regardless of diff noise or the no-baseline (New trade) path.
  const isOrder = determineIsOrder(trade);
  const status = (trade as any).status;
  const currentAutoAffirm = getActiveBooleanValue(settings.autoAffirm);
  const tradeWideChanged: Record<string, any> = {};
  if (
    isTradeWideSettingEditable("autoAffirm", status, isOrder) &&
    (!tradeWideBaseline || tradeWideBaseline.autoAffirm !== currentAutoAffirm)
  ) {
    tradeWideChanged.autoAffirm = currentAutoAffirm;
  }

  // LQC-1705: Cover Spread is per-CP but keeps its status ceiling — never send
  // it once the status has frozen it (mirrors the backend guard, RFQ-only,
  // editable through to Affirmed).
  const coverSpreadEditable = isTradeWideSettingEditable(
    "releaseCoverSpread",
    status,
    isOrder,
  );

  // LQC-2271 — Side / Direction are pre-Awarded only, and never apply to Orders
  // (always One-Way). Mirrors the sidebar, which hides both columns in exactly
  // those cases.
  const sideAndDirectionEditable =
    !isOrder && isSideAndDirectionEditable(status);

  // LQC-1707: derive the CPs to PATCH directly from the trade's execution
  // requests, not the shared counterpartiesList slice (owned by the Edit Panel's
  // CPList). The slide-in only edits settings of CPs already on the execution
  // strategy, and the loop below already resolves each CP's executionRequestId
  // from executionRequestCounterParties, so this is the correct source of truth.
  const activeCPs = buildCounterpartiesListFromTrade(trade);

  const payloads = activeCPs
    .map((cp: IToggleCounterpartiesButton) => {
      const er = (trade as any).executionRequestCounterParties?.find(
        (e: any) => e.provider === cp.code,
      );
      if (!er) return null;

      const currentOverride = settings.cpSettingsOverrides[cp.code] ?? {};
      const baselineOverride = cpBaselines?.[cp.code] ?? {};

      // A field is "changed" (and therefore sent) exactly when its cell is
      // coloured pending — mirroring resolveOverrideBorder so Update persists
      // precisely what the user sees highlighted:
      //   • override-pending (full orange): an explicit per-CP override that
      //     diverges from its baseline override; OR
      //   • following-pending (muted orange): no per-CP override, but the
      //     trade-level default this CP follows is itself pending — without this
      //     branch a CP that simply follows a changed default is never sent.
      // No baseline (New trade) → send everything.
      const isChangedForSend = (
        field:
          | "quoteType"
          | "releaseDirection"
          | "releaseCover"
          | "releaseCompetition"
          | "releaseCoverSpread"
          | "releaseSize",
      ): boolean => {
        if (!cpBaselines) return true;
        const overridePending = !isSameOverrideFieldValue(
          currentOverride[field],
          baselineOverride[field],
        );
        const followingPending =
          currentOverride[field] === undefined &&
          isTradeLevelFieldPending(settings, tradeLevelBaseline, field);
        return overridePending || followingPending;
      };

      const cpChanged: Record<string, any> = {};
      // LQC-2271 — Side / Direction, sent only while the trade is still quoting.
      // Past Awarded the side is released and the sidebar drops both columns, so
      // the status gate stops a stale diff leaking into the payload (same
      // reasoning as the trade-wide gate above).
      if (sideAndDirectionEditable) {
        if (isChangedForSend("quoteType")) {
          cpChanged.quoteType = (
            resolveSettingForCP(
              settings,
              cp.code,
              "quoteType",
            ) as IToggleButton[]
          ).find((option) => option.active)?.name;
        }
        if (isChangedForSend("releaseDirection")) {
          cpChanged.releaseDirection = getActiveBooleanValue(
            resolveSettingForCP(
              settings,
              cp.code,
              "releaseDirection",
            ) as IToggleButtonToReturnBoolean[],
          );
        }
      }
      if (isChangedForSend("releaseCover")) {
        cpChanged.releaseCover = getActiveBooleanValue(
          resolveSettingForCP(
            settings,
            cp.code,
            "releaseCover",
          ) as IToggleButtonToReturnBoolean[],
        );
      }
      if (isChangedForSend("releaseCompetition")) {
        cpChanged.releaseCompetition = getActiveBooleanValue(
          resolveSettingForCP(
            settings,
            cp.code,
            "releaseCompetition",
          ) as IToggleButtonToReturnBoolean[],
        );
      }
      // LQC-1705: Cover Spread is per-CP boolean — but only send while its
      // status ceiling allows it (RFQ-only, through to Affirmed).
      if (coverSpreadEditable && isChangedForSend("releaseCoverSpread")) {
        cpChanged.releaseCoverSpread = getActiveBooleanValue(
          resolveSettingForCP(
            settings,
            cp.code,
            "releaseCoverSpread",
          ) as IToggleButtonToReturnBoolean[],
        );
      }
      if (isChangedForSend("releaseSize")) {
        const releaseSizePct = resolveSettingForCP(
          settings,
          cp.code,
          "releaseSize",
        ) as number | undefined;
        cpChanged.releaseSize =
          typeof releaseSizePct === "number" ? releaseSizePct / 100 : undefined;
      }

      const allChanged = { ...tradeWideChanged, ...cpChanged };
      if (Object.keys(allChanged).length === 0) return null;

      return {
        esid,
        executionRequestId: er.executionRequestId,
        ...allChanged,
      };
    })
    .filter(Boolean);

  if (payloads.length === 0) {
    toast.info("No changes to apply.");
    return [];
  }

  await Promise.all(
    payloads.map((payload) =>
      fetchRequestWrapper(
        {
          url: "/Trade/Execution/Settings",
          method: "PATCH",
          objToSend: payload,
        },
        getAccessTokenSilently,
      ),
    ),
  );

  advanceEditBaselineFromCurrent(esid);

  // LQC-1842 / LQC-1843 — return the submitted per-CP payloads so the caller can
  // reconcile the request-information negotiation status (e.g. a released field
  // clearing a prior Reject, or a withdrawn field flipping Released → Rejected).
  // The reconcile lives in requestInformationStoreACTIONS; doing it here would
  // create an import cycle (that module already imports this one).
  return payloads;
}
